import { useCallback, useEffect, useRef } from 'react';
import { Animated, Platform, Pressable, StyleSheet } from 'react-native';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { Box } from './Box';
import { Text } from './Text';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  message: string;
  type?: ToastType;
  visible: boolean;
  onHide: () => void;
  duration?: number;
  /**
   * Makes the toast actionable. Given one, the bubble accepts touches and
   * dismisses itself on press; without one it stays inert, because a control
   * that looks tappable and does nothing is worse than plain text.
   */
  onPress?: () => void;
  /** Read by a screen reader in place of the raw message when the toast acts. */
  accessibilityLabel?: string;
}

export const Toast = ({
  message,
  type = 'info',
  visible,
  onHide,
  duration = 3000,
  onPress,
  accessibilityLabel,
}: ToastProps) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-100)).current;

  const hideToast = useCallback(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: -100,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onHide();
    });
  }, [opacity, translateY, onHide]);

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(translateY, {
          toValue: 0,
          useNativeDriver: true,
          tension: 65,
          friction: 8,
        }),
      ]).start();

      const timer = setTimeout(() => {
        hideToast();
      }, duration);

      return () => clearTimeout(timer);
    }
    hideToast();
  }, [visible, duration, hideToast, opacity, translateY]);

  const backgroundColor = {
    success: colors.status.success.main,
    error: colors.status.error.main,
    warning: colors.status.warning.main,
    info: colors.primary[500],
  }[type];

  return (
    <Animated.View
      testID="app_toast"
      // Inert unless it can do something: an untappable bubble must not swallow
      // a press meant for the screen underneath it.
      pointerEvents={onPress ? 'box-none' : 'none'}
      style={[
        styles.container,
        {
          opacity,
          transform: [{ translateY }],
        },
      ]}
      accessibilityLiveRegion="polite"
      accessibilityRole="alert"
    >
      <Pressable
        onPress={
          onPress
            ? () => {
                // Dismiss on press: leaving it up after it has been acted on
                // covers the screen it just navigated to.
                hideToast();
                onPress();
              }
            : undefined
        }
        disabled={!onPress}
        accessibilityRole={onPress ? 'button' : undefined}
        accessibilityLabel={accessibilityLabel ?? (onPress ? message : undefined)}
        testID={onPress ? 'app_toast_action' : undefined}
      >
        <Box
          backgroundColor={backgroundColor}
          paddingX="lg"
          paddingY="md"
          borderRadius="lg"
          style={styles.toast}
        >
          <Text variant="body" color={colors.text.primary} style={styles.message} weight="medium">
            {message}
          </Text>
        </Box>
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: Platform.select({ web: 80, native: 60 }),
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 9999,
    paddingHorizontal: spacing.md,
  },
  toast: {
    maxWidth: Platform.select({ web: 500, native: undefined }),
    width: '100%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  message: {
    textAlign: 'center',
  },
});
